/**
 * Public Paystub Intake Page — customer-facing form.
 * Route: `/paystub-intake` (generic, permanent link — same one for every client)
 *
 * Features
 * ────────
 * • Personal + employer address autocomplete (Photon / OSM — free, no key).
 *   Selecting a suggestion auto-fills city / state / ZIP; user can also type
 *   manually if they can't find their address.
 * • "I don't have job information" toggle (DEFAULT OFF — never auto-selected).
 * • Amount per check + "Before Taxes / After Taxes" toggle.
 * • Multi-stub selector (1-6) at the bottom, one pay-date field per stub.
 * • Submits to the generic endpoint so every client uses the same URL.
 */
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  CheckCircle2, Send, Building2, User, DollarSign, Calendar, Shield,
  ChevronDown, ChevronUp
} from "lucide-react";
import AddressAutocomplete from "../components/AddressAutocomplete";

const API = process.env.REACT_APP_BACKEND_URL;

const initial = {
  fullName: "", address: "", city: "", state: "", zip: "",
  ssnLastFour: "", email: "", phone: "",
  jobName: "", jobAddress: "", jobCity: "", jobState: "", jobZip: "",
  noJobInfo: false,                                // NEVER auto-selected
  grossPerCheck: "",
  amountType: "gross",                             // 'gross' (before taxes) | 'net' (after taxes)
  payFrequency: "biweekly",
  numStubs: 1,
  payDates: [""],                                  // one entry per stub
};

const CORE_REQUIRED = [
  "fullName", "address", "city", "state", "zip", "ssnLastFour",
  "grossPerCheck", "payFrequency",
];
const JOB_REQUIRED = ["jobName", "jobAddress", "jobCity", "jobState", "jobZip"];

export default function PaystubIntakePage() {
  const { token } = useParams();
  const isGeneric = !token;
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState(isGeneric ? "ready" : "loading");
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    if (isGeneric) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/paystub-intake/public/${token}`);
        if (res.data.already_submitted) setStatus("already");
        else setStatus("ready");
      } catch (e) {
        setStatus("invalid");
      }
    })();
  }, [token, isGeneric]);

  const set = (k) => (e) => {
    let v = e.target.value;
    if (k === "ssnLastFour") v = v.replace(/\D/g, "").slice(0, 4);
    if (["state", "jobState"].includes(k)) v = v.toUpperCase().slice(0, 2);
    if (["zip", "jobZip"].includes(k)) v = v.replace(/\D/g, "").slice(0, 5);
    setForm((f) => ({ ...f, [k]: v }));
  };

  const setNumStubs = (n) => {
    const clamped = Math.max(1, Math.min(6, n));
    setForm((f) => {
      const next = [...(f.payDates || [])];
      while (next.length < clamped) next.push("");
      next.length = clamped;
      return { ...f, numStubs: clamped, payDates: next };
    });
  };

  const setPayDate = (idx) => (e) => {
    const v = e.target.value;
    setForm((f) => {
      const dates = [...(f.payDates || [])];
      dates[idx] = v;
      return { ...f, payDates: dates };
    });
  };

  // Fill address helper — used by AddressAutocomplete
  const fillPersonalAddress = ({ line, city, state, zip }) => {
    setForm((f) => ({
      ...f,
      address: line ?? f.address,
      city: city ?? f.city,
      state: (state ?? f.state).toUpperCase().slice(0, 2),
      zip: (zip ?? f.zip).replace(/\D/g, "").slice(0, 5),
    }));
  };
  const fillJobAddress = ({ line, city, state, zip }) => {
    setForm((f) => ({
      ...f,
      jobAddress: line ?? f.jobAddress,
      jobCity: city ?? f.jobCity,
      jobState: (state ?? f.jobState).toUpperCase().slice(0, 2),
      jobZip: (zip ?? f.jobZip).replace(/\D/g, "").slice(0, 5),
    }));
  };

  const requiredList = form.noJobInfo ? CORE_REQUIRED : [...CORE_REQUIRED, ...JOB_REQUIRED];
  const missingBase = requiredList.filter((k) => !String(form[k] || "").trim());
  const validDates = (form.payDates || []).slice(0, form.numStubs).filter((d) => String(d || "").trim());
  const missingDatesCount = form.numStubs - validDates.length;
  const totalMissing = missingBase.length + missingDatesCount;
  const canSubmit =
    totalMissing === 0 &&
    String(form.ssnLastFour).length === 4 &&
    Number(form.grossPerCheck) > 0;

  const submit = async () => {
    setSubmitError("");
    if (!canSubmit) {
      setSubmitError(`Please fill in all required fields (${totalMissing} missing).`);
      return;
    }
    setStatus("submitting");
    try {
      const url = isGeneric
        ? `${API}/api/paystub-intake/public/generic/submit`
        : `${API}/api/paystub-intake/public/${token}/submit`;
      // Take the latest pay date as the "primary" for legacy compat.
      const sortedDates = [...validDates].sort().reverse();
      await axios.post(url, {
        ...form,
        grossPerCheck: Number(form.grossPerCheck),
        payDates: sortedDates,
        payDate: sortedDates[0] || "",
        payPeriodStart: "",
        payPeriodEnd: "",
      });
      setStatus("sent");
      toast.success("Your info has been submitted");
    } catch (e) {
      setSubmitError(e?.response?.data?.detail || "Submission failed. Please try again.");
      setStatus("ready");
    }
  };

  if (status === "loading") return <FullBleed title="Loading form…" />;
  if (status === "invalid") return <FullBleed title="This link is no longer valid" subtitle="Ask for a new intake link." tone="error" />;
  if (status === "already") return <FullBleed title="Already submitted ✓" subtitle="Your info was already received — thank you." tone="success" />;
  if (status === "sent") {
    return (
      <FullBleed
        title="Info received!"
        subtitle="Thanks — we'll take it from here. You can close this page."
        icon={<CheckCircle2 className="w-16 h-16 text-emerald-400" />}
        tone="success"
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-slate-950 text-white">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
        {/* Header */}
        <div className="mb-6 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-fuchsia-500/20 border border-fuchsia-500/40 text-fuchsia-200 text-xs font-bold mb-3">
            <Shield className="w-3.5 h-3.5" /> SECURE INTAKE FORM
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold">Paystub Info Intake</h1>
          <p className="text-sm text-zinc-400 mt-1">
            Fill in the fields below so your paystub can be prepared.
          </p>
        </div>

        <div className="space-y-4">
          {/* ─── Your Info ────────────────────────────────────── */}
          <Section icon={<User className="w-4 h-4 text-cyan-400" />} title="Your Info">
            <Field label="Full Name *" value={form.fullName} onChange={set("fullName")} testId="intake-fullname" />
            <AddressAutocomplete
              label="Home Address *"
              value={form.address}
              onChange={set("address")}
              onSelect={fillPersonalAddress}
              testId="intake-address"
            />
            <div className="grid grid-cols-6 gap-2">
              <div className="col-span-3"><Field label="City *" value={form.city} onChange={set("city")} testId="intake-city" /></div>
              <div className="col-span-1"><Field label="State *" value={form.state} onChange={set("state")} testId="intake-state" /></div>
              <div className="col-span-2"><Field label="ZIP *" value={form.zip} onChange={set("zip")} testId="intake-zip" /></div>
            </div>
            <Field label="Last 4 of Social *" value={form.ssnLastFour} onChange={set("ssnLastFour")}
              placeholder="4 digits" testId="intake-ssn" inputMode="numeric" />
            <div className="grid grid-cols-2 gap-2">
              <Field label="Email (optional)" value={form.email} onChange={set("email")} testId="intake-email" />
              <Field label="Phone (optional)" value={form.phone} onChange={set("phone")} testId="intake-phone" />
            </div>
          </Section>

          {/* ─── Job / Employer ───────────────────────────────── */}
          <Section icon={<Building2 className="w-4 h-4 text-orange-400" />} title="Job / Employer">
            {/* NOTE: default is OFF — never auto-selected */}
            <button
              type="button"
              onClick={() => setForm((f) => ({ ...f, noJobInfo: !f.noJobInfo }))}
              className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border-2 transition text-left ${
                form.noJobInfo
                  ? "border-amber-500 bg-amber-500/10"
                  : "border-zinc-700 bg-zinc-950 hover:border-zinc-500"
              }`}
              data-testid="intake-no-job-toggle"
            >
              <div className="flex items-center gap-2.5">
                <div className={`w-5 h-5 rounded flex items-center justify-center border-2 ${
                  form.noJobInfo ? "bg-amber-500 border-amber-500" : "border-zinc-600 bg-zinc-900"
                }`}>
                  {form.noJobInfo && <CheckCircle2 className="w-4 h-4 text-white" />}
                </div>
                <div>
                  <div className={`text-sm font-bold ${form.noJobInfo ? "text-amber-200" : "text-white"}`}>
                    I don&apos;t have job information
                  </div>
                  <div className="text-[11px] text-zinc-400 mt-0.5">
                    Skip this section — the admin will fill it in later
                  </div>
                </div>
              </div>
              {form.noJobInfo
                ? <ChevronDown className="w-4 h-4 text-amber-300" />
                : <ChevronUp className="w-4 h-4 text-zinc-500" />}
            </button>

            {form.noJobInfo ? (
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-100 text-xs" data-testid="intake-no-job-notice">
                Employer info has been marked as <b>Not Provided</b>. You can still submit — the admin will fill in the company name and address on their end.
              </div>
            ) : (
              <>
                <Field label="Company or Job Name *" value={form.jobName} onChange={set("jobName")} testId="intake-jobname" />
                <AddressAutocomplete
                  label="Job Address *"
                  value={form.jobAddress}
                  onChange={set("jobAddress")}
                  onSelect={fillJobAddress}
                  testId="intake-jobaddr"
                />
                <div className="grid grid-cols-6 gap-2">
                  <div className="col-span-3"><Field label="Job City *" value={form.jobCity} onChange={set("jobCity")} testId="intake-jobcity" /></div>
                  <div className="col-span-1"><Field label="State *" value={form.jobState} onChange={set("jobState")} testId="intake-jobstate" /></div>
                  <div className="col-span-2"><Field label="ZIP *" value={form.jobZip} onChange={set("jobZip")} testId="intake-jobzip" /></div>
                </div>
              </>
            )}
          </Section>

          {/* ─── Pay Info ─────────────────────────────────────── */}
          <Section icon={<DollarSign className="w-4 h-4 text-emerald-400" />} title="Pay Info">
            <Field label="How much do you make per check *"
              value={form.grossPerCheck} onChange={set("grossPerCheck")} testId="intake-gross"
              type="number" inputMode="decimal" prefix="$" placeholder="e.g. 2500" />

            {/* Before/After taxes toggle */}
            <div>
              <Label>Is that amount BEFORE or AFTER taxes? *</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, amountType: "gross" }))}
                  className={`px-3 py-2.5 rounded-lg border-2 text-sm font-bold transition ${
                    form.amountType === "gross"
                      ? "border-emerald-500 bg-emerald-500/15 text-emerald-100"
                      : "border-zinc-700 bg-zinc-900 text-zinc-400 hover:border-zinc-500"
                  }`}
                  data-testid="intake-amount-gross"
                >
                  <div className="text-[10px] uppercase tracking-wider opacity-80">Before Taxes</div>
                  <div>Gross</div>
                </button>
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, amountType: "net" }))}
                  className={`px-3 py-2.5 rounded-lg border-2 text-sm font-bold transition ${
                    form.amountType === "net"
                      ? "border-cyan-500 bg-cyan-500/15 text-cyan-100"
                      : "border-zinc-700 bg-zinc-900 text-zinc-400 hover:border-zinc-500"
                  }`}
                  data-testid="intake-amount-net"
                >
                  <div className="text-[10px] uppercase tracking-wider opacity-80">After Taxes</div>
                  <div>Net / Take-home</div>
                </button>
              </div>
            </div>

            <div>
              <Label>How often do you get paid? *</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                {[
                  { id: "weekly", label: "Weekly" },
                  { id: "biweekly", label: "Every 2 Weeks" },
                  { id: "semimonthly", label: "Twice / Month" },
                  { id: "monthly", label: "Once / Month" },
                ].map((o) => (
                  <button key={o.id} type="button"
                    onClick={() => setForm((f) => ({ ...f, payFrequency: o.id }))}
                    className={`px-3 py-2.5 rounded-lg border-2 text-sm font-bold transition ${
                      form.payFrequency === o.id
                        ? "border-emerald-500 bg-emerald-500/15 text-emerald-200"
                        : "border-zinc-700 bg-zinc-900 text-zinc-400 hover:border-zinc-500"
                    }`}
                    data-testid={`intake-freq-${o.id}`}>
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
          </Section>

          {/* ─── Pay Dates (bottom) ───────────────────────────── */}
          <Section icon={<Calendar className="w-4 h-4 text-fuchsia-400" />} title="Pay Dates">
            <div>
              <Label>How many paystubs do you need? *</Label>
              <div className="grid grid-cols-6 gap-1.5 mt-1">
                {[1, 2, 3, 4, 5, 6].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setNumStubs(n)}
                    className={`py-2.5 rounded-lg border-2 text-sm font-bold transition ${
                      form.numStubs === n
                        ? "border-fuchsia-500 bg-fuchsia-500/15 text-fuchsia-100"
                        : "border-zinc-700 bg-zinc-900 text-zinc-400 hover:border-zinc-500"
                    }`}
                    data-testid={`intake-numstubs-${n}`}
                  >
                    {n}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-zinc-500 mt-1">
                Pick a pay date for each one below. Latest date will appear at the top of the paystub list.
              </p>
            </div>

            <div className="space-y-2">
              {Array.from({ length: form.numStubs }).map((_, i) => (
                <Field
                  key={i}
                  label={`Pay Date #${i + 1} *`}
                  type="date"
                  value={form.payDates?.[i] || ""}
                  onChange={setPayDate(i)}
                  testId={`intake-paydate-${i}`}
                />
              ))}
            </div>
          </Section>

          {submitError && (
            <div className="p-3 rounded-lg bg-red-500/15 border border-red-500/40 text-red-200 text-sm"
              data-testid="intake-error">
              {submitError}
            </div>
          )}

          <div className="pt-2">
            <button onClick={submit} disabled={!canSubmit || status === "submitting"}
              className={`w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl text-base font-bold transition ${
                canSubmit && status !== "submitting"
                  ? "bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 hover:from-emerald-400 hover:via-teal-400 hover:to-cyan-400 text-white shadow-lg shadow-emerald-500/30"
                  : "bg-zinc-800 text-zinc-500 cursor-not-allowed"
              }`}
              data-testid="intake-submit">
              <Send className="w-5 h-5" />
              {status === "submitting"
                ? "Sending…"
                : (canSubmit ? "Submit My Info" : `Fill in ${totalMissing} more field${totalMissing !== 1 ? "s" : ""}`)}
            </button>
            <p className="text-[11px] text-zinc-500 text-center mt-2">
              Your info goes directly to the person who sent you this link.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   Presentational helpers
   ══════════════════════════════════════════════════════════════ */
function FullBleed({ title, subtitle, tone = "info", icon }) {
  const bg = tone === "success"
    ? "from-emerald-950 via-zinc-950 to-slate-950"
    : tone === "error"
      ? "from-red-950 via-zinc-950 to-slate-950"
      : "from-zinc-950 via-zinc-900 to-slate-950";
  return (
    <div className={`min-h-screen bg-gradient-to-br ${bg} text-white flex items-center justify-center px-4`}>
      <div className="max-w-md w-full text-center">
        {icon && <div className="flex justify-center mb-4">{icon}</div>}
        <h1 className="text-2xl font-bold">{title}</h1>
        {subtitle && <p className="text-sm text-zinc-400 mt-2">{subtitle}</p>}
      </div>
    </div>
  );
}

function Section({ icon, title, children }) {
  return (
    <div className="rounded-xl border border-zinc-700 bg-zinc-900/60 p-4">
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <h3 className="text-sm font-bold text-white">{title}</h3>
      </div>
      <div className="space-y-2.5">{children}</div>
    </div>
  );
}

function Label({ children }) {
  return <label className="text-[11px] font-bold uppercase tracking-wider text-zinc-400">{children}</label>;
}

function Field({ label, value, onChange, placeholder, testId, type = "text", inputMode, prefix }) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="relative mt-1">
        {prefix && (
          <span className="absolute inset-y-0 left-3 flex items-center text-zinc-500 text-sm">{prefix}</span>
        )}
        <input type={type} value={value} onChange={onChange} placeholder={placeholder}
          inputMode={inputMode}
          className={`w-full ${prefix ? "pl-7" : "px-3"} px-3 py-2 bg-zinc-950 border border-zinc-700 rounded-lg text-white text-sm placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-fuchsia-500`}
          data-testid={testId} />
      </div>
    </div>
  );
}
